import { create } from "zustand";
import { persist } from "zustand/middleware";

type Theme = "light" | "dark" | "system";

interface ThemeState {
    theme: Theme;
    setTheme: (theme: Theme) => void;
    toggleTheme: () => void;
}

const getSystemTheme = (): "light" | "dark" => {
    if (typeof window === "undefined") return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
};

const applyTheme = (theme: Theme) => {
    if (typeof window === "undefined") return;

    const systemTheme = getSystemTheme();
    const isDark =
        theme === "dark" || (theme === "system" && systemTheme === "dark");

    const root = document.documentElement;
    if (isDark) {
        root.classList.add("dark");
    } else {
        root.classList.remove("dark");
    }

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
        metaThemeColor.setAttribute("content", isDark ? "#1f2937" : "#ffffff");
    }
};

export const useThemeStore = create<ThemeState>()(
    persist(
        (set, get) => ({
            theme: "light",

            setTheme: (theme: Theme) => {
                set({ theme });
                applyTheme(theme);
            },

            toggleTheme: () => {
                const { theme } = get();
                const currentEffective =
                    theme === "system" ? getSystemTheme() : theme;
                const newTheme = currentEffective === "dark" ? "light" : "dark";
                get().setTheme(newTheme);
            },
        }),
        {
            name: "theme-storage",
            onRehydrateStorage: () => (state) => {
                if (state) {
                    applyTheme(state.theme);
                }
            },
        },
    ),
);

// Initialize theme on module load
if (typeof window !== "undefined") {
    const state = useThemeStore.getState();
    applyTheme(state.theme);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    mediaQuery.addEventListener("change", () => {
        const { theme } = useThemeStore.getState();
        if (theme === "system") {
            applyTheme(theme);
        }
    });
}
